/**
 * @module reporting ui
 */
import { createReducer } from '@reduxjs/toolkit';
import { uiActions } from '~/behaviors/actions/ui.actions';
import { trendsActions } from '~/behaviors/actions/trends.actions';
import { surveyReportingActions } from '~/behaviors/actions/survey_reporting.actions';
import { reportingActions } from '~/behaviors/actions/reporting.actions';
import { metaActions } from '~/behaviors/actions/meta.actions';
import { convert } from '~/tools/convert';
import { benchmarkActions } from '~/behaviors/actions/benchmark.actions';
import { employeeAttributeActions } from '~/behaviors/actions/employee_attribute.actions';

const defaultState = {
  isLoadingOverview: false,
  isLoadingTrends: false,
  isLoadingResults: false,
  isLoadingFullResponses: false,
  isLoadingEmployeeAttributes: false,
  error: null,

  // state based on new rest reporting endpoint
  isLoadingBaseReport: false,
  isLoadingBenchmarks: false,
  hasLoadedBenchmarks: false,
};

const reset = () => defaultState;

const merge = (updates) => (state) => ({
  ...state,
  ...updates,
});

const changeUI = (state, action) => {
  const updates = action?.payload || {};
  const sanitizedAttrs = convert.pick(updates, Object.keys(defaultState));

  return {
    ...state,
    ...sanitizedAttrs,
  };
};

const reportingUI = createReducer(defaultState, (builder) => {
  builder
    .addCase(metaActions.reset, reset)
    .addCase(uiActions.changeReportingUI, changeUI)
    .addCase(reportingActions.fetchFullResponses.pending, merge({ isLoadingFullResponses: true }))
    .addCase(reportingActions.fetchFullResponses.fulfilled, merge({ isLoadingFullResponses: false }))
    .addCase(reportingActions.fetchFullResponses.rejected, merge({ isLoadingFullResponses: false }))
    .addCase(trendsActions.fetchTrendResults.pending, merge({ isLoadingTrends: true }))
    .addCase(trendsActions.fetchTrendResults.fulfilled, merge({ isLoadingTrends: false }))
    .addCase(trendsActions.fetchTrendResults.rejected, merge({ isLoadingTrends: false }))
    .addCase(reportingActions.fetchQuestionResults.pending, merge({ isLoadingResults: true }))
    .addCase(reportingActions.fetchQuestionResults.fulfilled, merge({ isLoadingResults: false }))
    .addCase(reportingActions.fetchQuestionResults.rejected, merge({ isLoadingResults: false }))
    .addCase(
      reportingActions.fetchQuestionAttributeResponses.pending,
      merge({ isLoadingQuestionAttributeResponses: true }),
    )
    .addCase(
      reportingActions.fetchQuestionAttributeResponses.fulfilled,
      merge({ isLoadingQuestionAttributeResponses: false }),
    )
    .addCase(
      reportingActions.fetchQuestionAttributeResponses.rejected,
      merge({ isLoadingQuestionAttributeResponses: false }),
    )
    .addCase(surveyReportingActions.fetchReportingResults.pending, merge({ isLoadingBaseReport: true }))
    .addCase(surveyReportingActions.fetchReportingResults.fulfilled, merge({ isLoadingBaseReport: false }))
    .addCase(surveyReportingActions.fetchReportingResults.rejected, merge({ isLoadingBaseReport: false }))
    .addCase(benchmarkActions.fetchBenchmarks.pending, merge({ isLoadingBenchmarks: true }))
    .addCase(benchmarkActions.fetchBenchmarks.fulfilled, merge({ isLoadingBenchmarks: false }))
    .addCase(benchmarkActions.fetchBenchmarks.rejected, merge({ isLoadingBenchmarks: false }))
    .addCase(reportingActions.hasLoadedBenchmarks, merge({ hasLoadedBenchmarks: true }))
    .addCase(trendsActions.fetchContinuousSurveyTrendsAnalysis.pending, merge({ isLoadingTrends: true }))
    .addCase(trendsActions.fetchContinuousSurveyTrendsAnalysis.fulfilled, merge({ isLoadingTrends: false }))
    .addCase(trendsActions.fetchContinuousSurveyTrendsAnalysis.rejected, merge({ isLoadingTrends: false }))

    .addCase(employeeAttributeActions.fetchEmployeeAttributes.pending, merge({ isLoadingEmployeeAttributes: true }))
    .addCase(employeeAttributeActions.fetchEmployeeAttributes.fulfilled, merge({ isLoadingEmployeeAttributes: false }))
    .addCase(employeeAttributeActions.fetchEmployeeAttributes.rejected, merge({ isLoadingEmployeeAttributes: false }));
});

export default reportingUI;
